import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faHouseChimneyUser } from '@fortawesome/free-solid-svg-icons/faHouseChimneyUser';
import { faUsers } from '@fortawesome/free-solid-svg-icons/faUsers';
import { faHandshake } from '@fortawesome/free-solid-svg-icons/faHandshake';
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons/faCalendarDays';
import { faKey } from '@fortawesome/free-solid-svg-icons/faKey';
import { faPaw } from '@fortawesome/free-solid-svg-icons/faPaw';
import { faDumbbell } from '@fortawesome/free-solid-svg-icons/faDumbbell';
import { faTree } from '@fortawesome/free-solid-svg-icons/faTree';
import { faLaptopFile } from '@fortawesome/free-solid-svg-icons/faLaptopFile';
import { faBriefcase } from '@fortawesome/free-solid-svg-icons/faBriefcase';
import { faGraduationCap } from '@fortawesome/free-solid-svg-icons/faGraduationCap';
import { faHeart } from '@fortawesome/free-solid-svg-icons/faHeart';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useCtaColorVars } from '../hooks/useCtaColorVars';

// ============================================================================
// Narrative "who lives here" breakdown — a lead paragraph, then six resident
// archetype cards. Each card has an icon + title + bulleted list.
//
// Why icons instead of photos: lifestyle sections are per-property, and
// managing six distinct photos for every property is heavy. An editable
// FontAwesome icon name lets the editor (or the generator) pick a visual
// without the gallery-maintenance overhead.
//
// `resolveIcon` is a function-body switch rather than a top-level object
// literal on purpose — the schema linter's static extractor greedily grabs
// every `const X = { ... }` at module scope, and FA icon imports (used as
// shorthand property names in such an object) would break the extractor's
// eval context for the whole file. A switch keeps the lookup off its radar.
// Add new icons by importing them above and extending the switch.
// ============================================================================

function resolveIcon(name: string | null | undefined): IconDefinition {
  switch (name) {
    case 'faHouseChimneyUser': return faHouseChimneyUser;
    case 'faUsers': return faUsers;
    case 'faHandshake': return faHandshake;
    case 'faCalendarDays': return faCalendarDays;
    case 'faKey': return faKey;
    case 'faPaw': return faPaw;
    case 'faDumbbell': return faDumbbell;
    case 'faTree': return faTree;
    case 'faLaptopFile': return faLaptopFile;
    case 'faBriefcase': return faBriefcase;
    case 'faGraduationCap': return faGraduationCap;
    case 'faHeart': return faHeart;
    default: return faUsers; // safe generic fallback for unknown names
  }
}

const type1Def: SlotGroupDefinition = {
  label: 'Resident Type 1',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      defaultValue: 'faHouseChimneyUser',
      description:
        'Icon name from the FontAwesome bank. Options: faHouseChimneyUser, faUsers, faHandshake, faCalendarDays, faKey, faPaw, faDumbbell, faTree, faLaptopFile, faBriefcase, faGraduationCap, faHeart.',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Resident Lifestyle',
    },
    bullets: {
      label: 'Highlights',
      type: 'richtext',
      defaultValue:
        '<ul><li>Placeholder highlight — editor replaces per property</li><li>Second placeholder highlight</li><li>Third placeholder highlight</li></ul>',
    },
  },
  generator: {
    prompt:
      'Pick a resident archetype that genuinely fits this property (e.g. families, young professionals, remote workers, retirees, entrepreneurs, students, military). Write a short archetype title and 3–5 bulleted highlights grounded in the property\'s floor-plan mix, pet policy, amenities, and neighborhood. Each bullet ≤ 10 words. Pick the icon name from the curated bank that best matches the archetype — use "faHouseChimneyUser" for family/home, "faUsers" for community, "faHandshake" for rewards/connection, "faCalendarDays" for events, "faKey" for move-in/flexibility, "faPaw" for pet-friendly, "faDumbbell" for fitness, "faTree" for outdoors, "faLaptopFile" for remote work, "faBriefcase" for professionals/commute, "faGraduationCap" for students, "faHeart" for family care.',
    context:
      'Card 1 of 6 in the resident-types grid. Icon sits at the top of the card. Each sibling card covers a different archetype — do not duplicate the archetype, icon, or any bullet with cards 2–6.',
    length: 'Title ≤ 5 words. 3–5 bullets, each ≤ 10 words.',
  },
};

const type2Def: SlotGroupDefinition = {
  label: 'Resident Type 2',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      defaultValue: 'faUsers',
      description:
        'Icon name from the FontAwesome bank. Options: faHouseChimneyUser, faUsers, faHandshake, faCalendarDays, faKey, faPaw, faDumbbell, faTree, faLaptopFile, faBriefcase, faGraduationCap, faHeart.',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Resident Lifestyle',
    },
    bullets: {
      label: 'Highlights',
      type: 'richtext',
      defaultValue:
        '<ul><li>Placeholder highlight — editor replaces per property</li><li>Second placeholder highlight</li><li>Third placeholder highlight</li></ul>',
    },
  },
  generator: {
    prompt:
      'Pick a different resident archetype from card 1 that also fits this property. Write a short archetype title and 3–5 bulleted highlights grounded in the real amenity and neighborhood story here. Each bullet ≤ 10 words. Pick the icon name from the curated bank that best matches the archetype — use "faHouseChimneyUser" for family/home, "faUsers" for community, "faHandshake" for rewards/connection, "faCalendarDays" for events, "faKey" for move-in/flexibility, "faPaw" for pet-friendly, "faDumbbell" for fitness, "faTree" for outdoors, "faLaptopFile" for remote work, "faBriefcase" for professionals/commute, "faGraduationCap" for students, "faHeart" for family care.',
    context:
      'Card 2 of 6 in the resident-types grid. Icon sits at the top of the card. Must be a distinct archetype, icon, and highlight set from cards 1 and 3–6.',
    length: 'Title ≤ 5 words. 3–5 bullets, each ≤ 10 words.',
  },
};

const type3Def: SlotGroupDefinition = {
  label: 'Resident Type 3',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      defaultValue: 'faLaptopFile',
      description:
        'Icon name from the FontAwesome bank. Options: faHouseChimneyUser, faUsers, faHandshake, faCalendarDays, faKey, faPaw, faDumbbell, faTree, faLaptopFile, faBriefcase, faGraduationCap, faHeart.',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Resident Lifestyle',
    },
    bullets: {
      label: 'Highlights',
      type: 'richtext',
      defaultValue:
        '<ul><li>Placeholder highlight — editor replaces per property</li><li>Second placeholder highlight</li><li>Third placeholder highlight</li></ul>',
    },
  },
  generator: {
    prompt:
      'Pick a third resident archetype distinct from cards 1 and 2 that also fits this property. Write a short archetype title and 3–5 bulleted highlights grounded in what is real here. Each bullet ≤ 10 words. Pick the icon name from the curated bank that best matches the archetype — use "faHouseChimneyUser" for family/home, "faUsers" for community, "faHandshake" for rewards/connection, "faCalendarDays" for events, "faKey" for move-in/flexibility, "faPaw" for pet-friendly, "faDumbbell" for fitness, "faTree" for outdoors, "faLaptopFile" for remote work, "faBriefcase" for professionals/commute, "faGraduationCap" for students, "faHeart" for family care.',
    context:
      'Card 3 of 6 in the resident-types grid. Icon sits at the top of the card. Must be a distinct archetype, icon, and highlight set from cards 1, 2, 4, 5, 6.',
    length: 'Title ≤ 5 words. 3–5 bullets, each ≤ 10 words.',
  },
};

const type4Def: SlotGroupDefinition = {
  label: 'Resident Type 4',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      defaultValue: 'faTree',
      description:
        'Icon name from the FontAwesome bank. Options: faHouseChimneyUser, faUsers, faHandshake, faCalendarDays, faKey, faPaw, faDumbbell, faTree, faLaptopFile, faBriefcase, faGraduationCap, faHeart.',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Resident Lifestyle',
    },
    bullets: {
      label: 'Highlights',
      type: 'richtext',
      defaultValue:
        '<ul><li>Placeholder highlight — editor replaces per property</li><li>Second placeholder highlight</li><li>Third placeholder highlight</li></ul>',
    },
  },
  generator: {
    prompt:
      'Pick a fourth resident archetype distinct from cards 1–3 that also fits this property. Write a short archetype title and 3–5 bulleted highlights grounded in the amenity + neighborhood story. Each bullet ≤ 10 words. Pick the icon name from the curated bank that best matches the archetype — use "faHouseChimneyUser" for family/home, "faUsers" for community, "faHandshake" for rewards/connection, "faCalendarDays" for events, "faKey" for move-in/flexibility, "faPaw" for pet-friendly, "faDumbbell" for fitness, "faTree" for outdoors, "faLaptopFile" for remote work, "faBriefcase" for professionals/commute, "faGraduationCap" for students, "faHeart" for family care.',
    context:
      'Card 4 of 6 in the resident-types grid. Icon sits at the top of the card. Must be a distinct archetype, icon, and highlight set from cards 1, 2, 3, 5, 6.',
    length: 'Title ≤ 5 words. 3–5 bullets, each ≤ 10 words.',
  },
};

const type5Def: SlotGroupDefinition = {
  label: 'Resident Type 5',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      defaultValue: 'faBriefcase',
      description:
        'Icon name from the FontAwesome bank. Options: faHouseChimneyUser, faUsers, faHandshake, faCalendarDays, faKey, faPaw, faDumbbell, faTree, faLaptopFile, faBriefcase, faGraduationCap, faHeart.',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Resident Lifestyle',
    },
    bullets: {
      label: 'Highlights',
      type: 'richtext',
      defaultValue:
        '<ul><li>Placeholder highlight — editor replaces per property</li><li>Second placeholder highlight</li><li>Third placeholder highlight</li></ul>',
    },
  },
  generator: {
    prompt:
      'Pick a fifth resident archetype distinct from cards 1–4 that also fits this property. Write a short archetype title and 3–5 bulleted highlights. Each bullet ≤ 10 words. Pick the icon name from the curated bank that best matches the archetype — use "faHouseChimneyUser" for family/home, "faUsers" for community, "faHandshake" for rewards/connection, "faCalendarDays" for events, "faKey" for move-in/flexibility, "faPaw" for pet-friendly, "faDumbbell" for fitness, "faTree" for outdoors, "faLaptopFile" for remote work, "faBriefcase" for professionals/commute, "faGraduationCap" for students, "faHeart" for family care.',
    context:
      'Card 5 of 6 in the resident-types grid. Icon sits at the top of the card. Must be a distinct archetype, icon, and highlight set from cards 1, 2, 3, 4, 6.',
    length: 'Title ≤ 5 words. 3–5 bullets, each ≤ 10 words.',
  },
};

const type6Def: SlotGroupDefinition = {
  label: 'Resident Type 6',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      defaultValue: 'faHandshake',
      description:
        'Icon name from the FontAwesome bank. Options: faHouseChimneyUser, faUsers, faHandshake, faCalendarDays, faKey, faPaw, faDumbbell, faTree, faLaptopFile, faBriefcase, faGraduationCap, faHeart.',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Resident Lifestyle',
    },
    bullets: {
      label: 'Highlights',
      type: 'richtext',
      defaultValue:
        '<ul><li>Placeholder highlight — editor replaces per property</li><li>Second placeholder highlight</li><li>Third placeholder highlight</li></ul>',
    },
  },
  generator: {
    prompt:
      'Pick a sixth resident archetype distinct from cards 1–5 that also fits this property. Write a short archetype title and 3–5 bulleted highlights. Each bullet ≤ 10 words. Pick the icon name from the curated bank that best matches the archetype — use "faHouseChimneyUser" for family/home, "faUsers" for community, "faHandshake" for rewards/connection, "faCalendarDays" for events, "faKey" for move-in/flexibility, "faPaw" for pet-friendly, "faDumbbell" for fitness, "faTree" for outdoors, "faLaptopFile" for remote work, "faBriefcase" for professionals/commute, "faGraduationCap" for students, "faHeart" for family care.',
    context:
      'Card 6 of 6 in the resident-types grid. Icon sits at the top of the card. Must be a distinct archetype, icon, and highlight set from cards 1–5.',
    length: 'Title ≤ 5 words. 3–5 bullets, each ≤ 10 words.',
  },
};

// ============================================================================
// Reusable resident-type card
// ============================================================================

interface ResidentTypeCardProps {
  path: string;
  definition: SlotGroupDefinition;
  index: number;
}

function ResidentTypeCard({ path, definition, index }: ResidentTypeCardProps) {
  const type = useSlotGroup(path, definition);
  const ref = useScrollReveal<HTMLDivElement>();
  const icon = resolveIcon(type.icon);

  return (
    <article
      ref={ref}
      className="reveal-fade-up lrt-card"
      style={{ transitionDelay: `${index * 70}ms` }}
    >
      <EditableSlotGroup groupPath={path} definition={definition}>
        <div className="lrt-card-inner">
          <div className="lrt-card-icon" aria-hidden="true">
            <FontAwesomeIcon icon={icon} />
          </div>
          {type.title ? <h3 className="lrt-card-title">{type.title}</h3> : null}
          {type.bullets ? (
            <div
              className="lrt-card-bullets"
              data-vibesite-richtext=""
              dangerouslySetInnerHTML={{ __html: type.bullets }}
            />
          ) : null}
        </div>
      </EditableSlotGroup>
    </article>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function LifestyleResidentTypes() {
  useRevealStyles();
  const ctaColorVars = useCtaColorVars();

  const types: { path: string; def: SlotGroupDefinition }[] = [
    { path: 'lifestyleresidenttypes.type_1', def: type1Def },
    { path: 'lifestyleresidenttypes.type_2', def: type2Def },
    { path: 'lifestyleresidenttypes.type_3', def: type3Def },
    { path: 'lifestyleresidenttypes.type_4', def: type4Def },
    { path: 'lifestyleresidenttypes.type_5', def: type5Def },
    { path: 'lifestyleresidenttypes.type_6', def: type6Def },
  ];

  return (
    <section className="lrt-section" style={ctaColorVars}>
      <div className="lrt-container">
        <div className="lrt-header">
          <RichTextContent
            path="lifestyleresidenttypes.eyebrow"
            label="Eyebrow"
            defaultValue="<p>Who Lives Here</p>"
            as="div"
            className="lrt-eyebrow"
          />
          <RichTextContent
            path="lifestyleresidenttypes.heading"
            label="Heading"
            defaultValue="<p>A Community for Every Kind of Resident</p>"
            as="h2"
            className="lrt-heading"
          />
          <RichTextContent
            path="lifestyleresidenttypes.intro"
            label="Intro"
            defaultValue='<p>Residents at <span data-type="field-token" data-field="property_name"></span> come from every walk of life. Here is a snapshot of the people who already call this community home — and the parts of life here that keep them.</p>'
            as="div"
            className="lrt-intro"
          />
        </div>

        <div className="lrt-grid">
          {types.map((type, i) => (
            <ResidentTypeCard
              key={type.path}
              path={type.path}
              definition={type.def}
              index={i}
            />
          ))}
        </div>

        {/* Schema-lint anchors — hidden, literal paths for each card so the
            source-level linter can match `slot-group-missing-editable-wrapper`.
            See src/components/LifestyleFeatureRows.tsx for the same pattern. */}
        <EditableSlotGroup
          groupPath="lifestyleresidenttypes.type_1"
          definition={type1Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="lifestyleresidenttypes.type_2"
          definition={type2Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="lifestyleresidenttypes.type_3"
          definition={type3Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="lifestyleresidenttypes.type_4"
          definition={type4Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="lifestyleresidenttypes.type_5"
          definition={type5Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="lifestyleresidenttypes.type_6"
          definition={type6Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
      </div>

      <style>{`
        .lrt-section {
          position: relative;
          background: var(--vibe-color-surface, #f4f2ee);
          padding: 5rem 1.5rem 5rem;
        }
        .lrt-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .lrt-header {
          text-align: center;
          margin-bottom: 3.5rem;
        }
        .lrt-eyebrow,
        .lrt-eyebrow * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .lrt-eyebrow {
          color: var(--vibe-color-secondary);
          font-size: 0.78rem;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          margin: 0 0 0.75rem;
        }
        .lrt-eyebrow p { margin: 0; }

        .lrt-heading,
        .lrt-heading * {
          font-family: var(--vibe-font-heading);
          font-weight: 400;
        }
        .lrt-heading {
          color: var(--vibe-color-secondary, #3E441D);
          font-size: clamp(1.9rem, 4vw, 2.75rem);
          line-height: 1.1;
          letter-spacing: 0.03em;
          margin: 0 auto 1.25rem;
          max-width: 36rem;
        }

        .lrt-intro,
        .lrt-intro * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .lrt-intro {
          color: rgba(17, 24, 39, 0.75);
          font-size: 1rem;
          line-height: 1.65;
          max-width: 48rem;
          margin: 0 auto;
        }
        @media (min-width: 768px) {
          .lrt-intro { font-size: 1.0625rem; }
        }
        .lrt-intro p { margin: 0 0 0.6rem; }
        .lrt-intro p:last-child { margin-bottom: 0; }

        /* ── Resident type grid ── */
        .lrt-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.75rem;
        }
        @media (min-width: 640px) {
          .lrt-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (min-width: 1024px) {
          .lrt-grid { grid-template-columns: 1fr 1fr 1fr; gap: 2rem; }
        }

        .lrt-card {
          position: relative;
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border: 1px solid rgba(17, 24, 39, 0.06);
          box-shadow: 0 4px 20px -12px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
                      box-shadow 0.4s ease,
                      border-color 0.4s ease;
        }
        .lrt-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 36px -16px rgba(0, 0, 0, 0.22);
          border-color: var(--vibe-color-secondary);
        }

        .lrt-card-inner {
          position: relative;
          padding: 2rem 1.75rem 1.85rem;
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
        }

        /* Matches the BetterBuiltIntro benefit-card icon tile. */
        .lrt-card-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          align-self: flex-start;
          width: 3.75rem;
          height: 3.75rem;
          background: color-mix(in srgb, var(--cta-bg, var(--vibe-color-primary)) 10%, #ffffff);
          color: var(--cta-bg, var(--vibe-color-primary));
          font-size: 1.6rem;
          margin: 0 0 1.25rem;
        }

        .lrt-card-title {
          margin: 0 0 1rem;
          font-family: var(--vibe-font-heading);
          font-weight: 400;
          font-size: 1.25rem;
          line-height: 1.2;
          color: var(--vibe-color-secondary, #3E441D);
          letter-spacing: 0.02em;
        }

        .lrt-card-bullets,
        .lrt-card-bullets * {
          font-family: var(--vibe-font-body);
        }
        .lrt-card-bullets {
          font-size: 0.9rem;
          line-height: 1.55;
          color: rgba(17, 24, 39, 0.78);
        }
        .lrt-card-bullets ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
        }
        .lrt-card-bullets li {
          position: relative;
          padding-left: 1.1rem;
        }
        .lrt-card-bullets li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.6em;
          width: 5px;
          height: 5px;
          background: var(--vibe-color-secondary);
          border-radius: 50%;
        }
        .lrt-card-bullets ol {
          list-style: decimal;
          margin: 0;
          padding-left: 1.25rem;
        }
        .lrt-card-bullets ol li { padding-left: 0.25rem; }
        .lrt-card-bullets ol li::before { display: none; }
        .lrt-card-bullets p { margin: 0 0 0.5rem; }
        .lrt-card-bullets p:last-child { margin-bottom: 0; }

        @media (max-width: 639px) {
          .lrt-section { padding: 3.5rem 1rem 3.5rem; }
          .lrt-header { margin-bottom: 2.25rem; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LifestyleResidentTypes',
  displayName: 'Lifestyle Resident Types',
  tokens: {
    'lifestyleresidenttypes.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Who Lives Here</p>',
    },
    'lifestyleresidenttypes.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>A Community for Every Kind of Resident</p>',
    },
    'lifestyleresidenttypes.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        '<p>Residents at <span data-type="field-token" data-field="property_name"></span> come from every walk of life. Here is a snapshot of the people who already call this community home — and the parts of life here that keep them.</p>',
      generator: {
        prompt:
          'Write 2–3 sentences describing who already lives at this property. Base it on the real resident mix (floor-plan mix, pet policy, neighborhood vibe). If the surrounding city has a clear economic character — tech, oil & gas, university, retirement, military, agriculture — name it.',
        context:
          'Lead paragraph above the resident-type grid. The six cards below each cover one archetype with an icon + bullets.',
        length: '2–3 sentences.',
      },
    },
  },
  slotGroups: {
    'lifestyleresidenttypes.type_1': type1Def,
    'lifestyleresidenttypes.type_2': type2Def,
    'lifestyleresidenttypes.type_3': type3Def,
    'lifestyleresidenttypes.type_4': type4Def,
    'lifestyleresidenttypes.type_5': type5Def,
    'lifestyleresidenttypes.type_6': type6Def,
  },
};

(LifestyleResidentTypes as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default LifestyleResidentTypes;
